import React, { useState, useCallback, useMemo } from 'react';
import HeroSection from '../components/HeroSection';
import Picture from '../components/Picture';
import Lightbox from '../components/Lightbox';
import SEO from '../components/SEO';
import { Play } from 'lucide-react';

const HERO_IMG = '/images/socials-hero.jpg';
const SKINFLINT_MAIN = '/images/skinflint-2021-main.jpg';

const WOODLAND_GAMES_2023 = Array.from({ length: 8 }, (_, i) => ({
  src: `/images/woodland-games-2023-${i + 1}.jpg`,
  caption: 'Woodland Games 2023',
  type: 'image',
}));

// 38 photos plus the rally video as the final tile. The video file is served
// from /videos/, the still frame is used as the thumbnail/poster.
const SKINFLINT_2021 = [
  ...Array.from({ length: 38 }, (_, i) => ({
    src: `/images/skinflint-2021-${i + 1}.jpg`,
    caption: 'Skinflint Rally 2021',
    type: 'image',
  })),
  {
    src: '/videos/skinflint-2021.mp4',
    thumb: '/images/skinflint-2021-39.jpg',
    caption: 'Skinflint Rally 2021 — Highlight Reel',
    type: 'video',
  },
];

function GallerySection({
  id,
  testId,
  bgClass,
  eyebrow,
  title,
  description,
  intro,
  photos,
}) {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const total = photos.length;

  const openLightbox = useCallback((i) => setLightboxIndex(i), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prev = useCallback(() => setLightboxIndex((i) => (i <= 0 ? total - 1 : i - 1)), [total]);
  const next = useCallback(() => setLightboxIndex((i) => (i >= total - 1 ? 0 : i + 1)), [total]);

  const images = useMemo(() => photos.map((p) => p.src), [photos]);
  const captions = useMemo(() => photos.map((p) => p.caption), [photos]);
  const types = useMemo(() => photos.map((p) => p.type), [photos]);

  return (
    <section id={id} data-testid={testId} className={`py-20 sm:py-28 scroll-mt-24 ${bgClass}`}>
      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          captions={captions}
          types={types}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prev}
          onNext={next}
        />
      )}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-4">
            {eyebrow}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-6">
            {title}
          </h2>
          {description && (
            <div className="max-w-3xl mx-auto space-y-4 text-base text-[#52525B] leading-relaxed text-left sm:text-center">
              {description}
            </div>
          )}
        </div>

        {intro}

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {photos.map((photo, i) => (
            <button
              key={photo.src}
              data-testid={`${testId}-image-${i}`}
              onClick={() => openLightbox(i)}
              className="overflow-hidden cursor-pointer group focus:outline-none focus:ring-2 focus:ring-[#F0A500] relative"
            >
              <Picture
                src={photo.thumb || photo.src}
                alt={`${photo.caption} — ${photo.type === 'video' ? 'video' : `photo ${i + 1}`}`}
                className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
              />
              {photo.type === 'video' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                  <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
                    <Play className="w-7 h-7 text-[#F0A500] ml-1" fill="currentColor" />
                  </div>
                </div>
              )}
              {photo.type !== 'video' && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
              )}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function SocialsPage() {
  return (
    <div data-testid="socials-page">
      <SEO
        title="Socials"
        description="Members' socials and weekends away from Glossop & District Round Table 916 — the Woodland Games, the Skinflint Rally, beer festivals, and the events that build friendships for life."
        path="/socials"
        keywords={['Round Table socials', 'Woodland Games 2023', 'Skinflint Rally 2021', 'North Coast 500', 'Glossop Round Table weekend']}
      />

      <HeroSection
        image={HERO_IMG}
        title="Socials"
        subtitle="The weekends, away days and good times that make Round Table what it is"
        fullHeight={false}
      />

      {/* Woodland Games 2023 */}
      <GallerySection
        id="about-woodland-games-section"
        testId="socials-woodland-games"
        bgClass="bg-[#F4F4F5]"
        eyebrow="Members' Highlights"
        title="The Woodland Games 2023"
        description={
          <>
            <p>
              In July 2023 a team from Glossop &amp; District Round Table headed off for a weekend at the Woodland Games — a much-loved gathering that brings together Tablers from clubs across the country for friendly competition, the great outdoors and more than a few laughs around the campfire.
            </p>
            <p>
              Across the weekend, members tackled woodland challenges including paintball, axe throwing, archery and tug-of-war, swapped stories over an open-fire feast, and rounded the night off with live music well into the small hours. It captured everything Round Table is about: fun, friendship and a healthy dose of #DoMore spirit — proof that the best memories often start with a tent, a fire and a good bunch of mates.
            </p>
          </>
        }
        photos={WOODLAND_GAMES_2023}
      />

      {/* Skinflint Rally 2021 */}
      <section
        id="skinflint-rally-2021"
        data-testid="socials-skinflint-2021-section"
        className="py-20 sm:py-28 bg-white scroll-mt-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-12">
            <div>
              <Picture
                src={SKINFLINT_MAIN}
                alt="The 2021 Skinflint Rally — Coventry No. 23 Round Table poster art"
                className="w-full h-auto object-cover"
              />
            </div>
            <div>
              <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#F0A500] mb-4 block">
                Members&apos; Highlights
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-6">
                The 2021 Skinflint Rally — North Coast 500
              </h2>
              <div className="space-y-4 text-base text-[#52525B] leading-relaxed">
                <p>
                  In September 2021, a team from Glossop &amp; District Round Table swapped their day jobs for the driving seat of a free-of-charge Ford Focus and joined the Skinflint Rally — the budget banger challenge run by Round Table Great Britain &amp; Ireland. The rules were simple: buy a car for £500 or less (or, if you&apos;re lucky, blag a freebie), dress it up, and survive a route that very few sensible people would attempt voluntarily.
                </p>
                <p>
                  Over four days (9&ndash;12 September), teams set off from Whitehaven in Cumbria, crossed the border into Scotland, tackled the full North Coast 500 &mdash; single-track roads, mountain passes, sea-cliff views and the inevitable mid-Highlands breakdown &mdash; before limping triumphantly into Newcastle. Daily challenges along the way clocked up the points, but the real scoreboard was the fundraising one: between them, the rally raised an astonishing <strong>£35,224.62</strong> for Myton Hospice, the Harley Jae Trust and a host of local community groups. Coast to coast, banger to banger, and very much in the spirit of #DoMore.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Gallery (separate state so it doesn't collide with the description column) */}
        <SkinflintGallery />
      </section>
    </div>
  );
}

function SkinflintGallery() {
  const photos = SKINFLINT_2021;
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const total = photos.length;
  const openLightbox = useCallback((i) => setLightboxIndex(i), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prev = useCallback(() => setLightboxIndex((i) => (i <= 0 ? total - 1 : i - 1)), [total]);
  const next = useCallback(() => setLightboxIndex((i) => (i >= total - 1 ? 0 : i + 1)), [total]);

  const images = useMemo(() => photos.map((p) => p.src), [photos]);
  const captions = useMemo(() => photos.map((p) => p.caption), [photos]);
  const types = useMemo(() => photos.map((p) => p.type), [photos]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          captions={captions}
          types={types}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prev}
          onNext={next}
        />
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {photos.map((photo, i) => (
          <button
            key={photo.src}
            data-testid={`socials-skinflint-image-${i}`}
            onClick={() => openLightbox(i)}
            className="overflow-hidden cursor-pointer group focus:outline-none focus:ring-2 focus:ring-[#F0A500] relative"
          >
            <Picture
              src={photo.thumb || photo.src}
              alt={`Skinflint Rally 2021 — ${photo.type === 'video' ? 'video' : `photo ${i + 1}`}`}
              className="w-full h-44 object-cover transition-transform duration-300 group-hover:scale-105"
            />
            {photo.type === 'video' && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
                  <Play className="w-7 h-7 text-[#F0A500] ml-1" fill="currentColor" />
                </div>
              </div>
            )}
            {photo.type !== 'video' && (
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
